import axios from 'axios'
import request from '@/utils/request'

const BASE_URL = '/api/v1/admin/launcher-update'

const ERROR_MESSAGES = {
  LAUNCHER_UPDATE_REVISION_CONFLICT: '配置已被他人修改，请刷新后重试',
  LAUNCHER_UPDATE_HOST_NOT_ALLOWED: '地址不在平台白名单内，请检查线路配置',
  LAUNCHER_UPDATE_VERSION_NOT_SYNCED: '最低支持版本尚未在所有已启用线路同步，请先在 Builder 同步',
  LAUNCHER_UPDATE_CONFIRM_REQUIRED: '此策略变更需要输入“强制更新”确认',
  LAUNCHER_UPDATE_INVALID: '配置内容不符合要求，请检查填写内容',
  LAUNCHER_UPDATE_NOT_FOUND: '公告不存在或已被删除，请刷新列表'
}

function preserveBusinessError(body) {
  // 公共拦截器只保留错误文案；在其执行前保留本模块的业务码。
  if (body && String(body.code || '').startsWith('LAUNCHER_UPDATE_')) {
    const error = new Error(ERROR_MESSAGES[body.code] || body.msg || '启动器更新操作失败')
    error.code = body.code
    error.msg = error.message
    error.data = body.data
    throw error
  }
  return body
}

function unwrap(response) {
  const body = response && response.status !== undefined && response.data !== undefined
    ? response.data
    : response
  preserveBusinessError(body)
  return body && body.code !== undefined && body.data !== undefined ? body.data : body
}

function isObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function objectItems(value) {
  return Array.isArray(value) && value.every(isObject)
}

function stringItems(value) {
  return Array.isArray(value) && value.every(item => typeof item === 'string')
}

function validConfig(data) {
  if (!isObject(data) || !isObject(data.sources) || !isObject(data.policy)) return false
  const sources = data.sources
  const policy = data.policy
  return Number.isInteger(data.revision) && data.revision >= 0 &&
    ['github', 'r2', 'fallback'].every(id => isObject(sources[id]) && typeof sources[id].enabled === 'boolean') &&
    ['normal', 'force'].includes(policy.mode) && stringItems(policy.blocked_versions) &&
    typeof policy.message === 'string' && typeof policy.block_unreported === 'boolean' &&
    policy.running_grace_seconds === 300 && stringItems(data.allowed_hosts)
}

function validPage(data) {
  return isObject(data) && Number.isInteger(data.total) && data.total >= 0 && objectItems(data.items)
}

function call(options, validate = isObject) {
  return request({
    timeout: 60000,
    ...options,
    transformResponse: [].concat(axios.defaults.transformResponse, preserveBusinessError)
  }).then(unwrap).then(data => {
    if (!validate(data)) throw new Error('平台返回的数据格式不符合启动器更新契约，请稍后重试')
    return data
  })
}

export function launcherUpdateErrorMessage(error, fallback = '读取启动器更新信息失败') {
  const body = error && error.response && error.response.data
  const code = error && error.code || body && body.code
  if (ERROR_MESSAGES[code]) return ERROR_MESSAGES[code]
  if (error && error.code === 'ECONNABORTED') return '请求超时，请稍后重试'
  if (error && error.isAxiosError && !error.response) return '无法连接平台，请检查网络后重试'
  return body && body.msg || error && error.message || fallback
}

export function getLauncherUpdateConfig() {
  return call({ url: `${BASE_URL}/config`, method: 'get' }, validConfig)
}

export function saveLauncherUpdateConfig(data) {
  return call({ url: `${BASE_URL}/config`, method: 'put', data }, validConfig)
}

export function getLauncherUpdateStatus(refresh = false) {
  return call({ url: `${BASE_URL}/status`, method: 'get', params: { refresh } },
    data => isObject(data) && objectItems(data.sources))
}

export function listLauncherUpdateReleases() {
  return call({ url: `${BASE_URL}/releases`, method: 'get' },
    data => isObject(data) && typeof data.reachable === 'boolean' && objectItems(data.items))
}

export function getLauncherDeviceSummary() {
  return call({ url: `${BASE_URL}/devices/summary`, method: 'get' },
    data => isObject(data) && Number.isInteger(data.total) && data.total >= 0 && objectItems(data.versions))
}

export function listLauncherDevices(params) {
  return call({ url: `${BASE_URL}/devices`, method: 'get', params }, validPage)
}

export function getLauncherUpdateImpact(policy) {
  return call({
    url: `${BASE_URL}/impact`,
    method: 'get',
    params: {
      min_version: policy.min_version || undefined,
      blocked_versions: policy.blocked_versions.join(','),
      block_unreported: policy.block_unreported
    }
  }, data => isObject(data) && ['active_total', 'affected', 'affected_install_failed', 'unreported']
    .every(field => Number.isInteger(data[field]) && data[field] >= 0))
}

export function listLauncherUpdateAudits(params) {
  return call({ url: `${BASE_URL}/audits`, method: 'get', params }, validPage)
}

export function listLauncherAnnouncements(params) {
  return call({ url: `${BASE_URL}/announcements`, method: 'get', params },
    data => validPage(data) && data.items.every(item => stringItems(item.display)))
}

export function createLauncherAnnouncement(data) {
  return call({ url: `${BASE_URL}/announcements`, method: 'post', data })
}

export function updateLauncherAnnouncement(id, data) {
  return call({ url: `${BASE_URL}/announcements/${encodeURIComponent(id)}`, method: 'put', data })
}

export function deleteLauncherAnnouncement(id) {
  return call({ url: `${BASE_URL}/announcements/${encodeURIComponent(id)}`, method: 'delete' }, () => true)
}
